/**
 * Application Configuration
 *
 * Single source of truth for environment-derived configuration. This is the
 * only module that reads custom `VITE_*` variables and runtime config.
 *
 * The API base URL resolves from two layers, in precedence order:
 *  1. Runtime config — `/config.json`, fetched once at startup by
 *     {@link loadConfig} and mounted per-environment from a Kubernetes
 *     ConfigMap. This lets a single immutable image be promoted across
 *     environments without a rebuild.
 *  2. Build-time env — the `VITE_API_BASE_URL` variable, used for local
 *     development and as the fallback when no `/config.json` is served.
 *
 * Values are exposed as functions (not module-level constants) so they are
 * read lazily — overridable in tests via `vi.stubEnv` and not frozen at
 * import time.
 *
 * @module config
 */

// =============================================================================
// API Base URL
// =============================================================================

/**
 * Default API base URL, used when neither `/config.json` nor
 * `VITE_API_BASE_URL` supplies a value.
 *
 * Points at the shared test platform. Deployed environments override this at
 * runtime via `/config.json`; local development can override it via the
 * `VITE_API_BASE_URL` environment variable.
 */
export const DEFAULT_API_BASE_URL =
  'https://api.workload.test-plat.modernpath.ai';

/**
 * Shape of the runtime configuration document served at `/config.json`.
 */
interface RuntimeConfig {
  /** Base URL prepended to every API request. Empty string = relative paths. */
  apiBaseUrl?: string;

  /**
   * Debug flag. When truthy (`1`, `true`, `"1"`, or `"true"`), the SPA renders
   * extra diagnostic views — notably the raw ID-token claims and userinfo on
   * the profile page. See {@link isDebugEnabled}.
   */
  debug?: boolean | number | string;
}

/**
 * Runtime config loaded from `/config.json`. `null` until {@link loadConfig}
 * has resolved (or if it was never called, e.g. in unit tests).
 */
let runtimeConfig: RuntimeConfig | null = null;

/**
 * Fetches `/config.json` once and caches it for subsequent {@link getApiBaseUrl}
 * calls. Must be awaited at startup (see `main.tsx`) before any API request.
 *
 * The file is mounted per-environment from a ConfigMap. It is absent in local
 * dev and preview builds — a 404 is expected and silently falls back to the
 * build-time env layer. Any failure is non-fatal: resolution degrades to
 * `VITE_API_BASE_URL` / {@link DEFAULT_API_BASE_URL}.
 *
 * @param fetchFn - Injectable fetch implementation (for testing).
 */
export async function loadConfig(
  fetchFn: typeof fetch = fetch,
): Promise<void> {
  try {
    const response = await fetchFn('/config.json', { cache: 'no-store' });
    if (!response.ok) return;
    runtimeConfig = (await response.json()) as RuntimeConfig;
  } catch (error) {
    console.warn(
      '[config] could not load /config.json; using build-time defaults',
      error,
    );
  }
}

/**
 * Returns the configured API base URL, normalized without a trailing slash.
 *
 * Prepended to every API request — both the BFF (`/oauth-agent/*`) and the
 * API proxy (`/api/*`). Resolution order:
 *
 *   1. `apiBaseUrl` from runtime `/config.json` (see {@link loadConfig})
 *   2. the `VITE_API_BASE_URL` build-time environment variable
 *   3. {@link DEFAULT_API_BASE_URL}
 *
 * An empty string from either layer is preserved and means "use relative
 * paths against the same origin that serves the SPA".
 */
export function getApiBaseUrl(): string {
  const base =
    runtimeConfig?.apiBaseUrl ??
    import.meta.env.VITE_API_BASE_URL ??
    DEFAULT_API_BASE_URL;
  return base.replace(/\/+$/, '');
}

/**
 * Test-only: clears cached runtime config so each test starts from a clean
 * slate. Not used by application code.
 */
export function _resetRuntimeConfig(): void {
  runtimeConfig = null;
}

// =============================================================================
// Debug Mode
// =============================================================================

/**
 * Whether a flag value (from JSON config or an env var) counts as "enabled".
 *
 * Accepts both the JSON forms (`true`, `1`) and the string forms produced by
 * environment variables (`"1"`, `"true"`). Anything else — including `false`,
 * `0`, `"0"`, `""`, and `undefined` — counts as disabled.
 */
function isFlagEnabled(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

/**
 * Returns whether debug mode is enabled.
 *
 * Debug mode surfaces extra diagnostic views in the UI — notably the raw
 * ID-token claims and userinfo on the profile page. It resolves, like the API
 * base URL, from two layers in precedence order:
 *
 *   1. `debug` from runtime `/config.json` (see {@link loadConfig}), set
 *      per-environment via the mounted ConfigMap.
 *   2. the `VITE_DEBUG` build-time environment variable (local development).
 *
 * A value of `1`, `true`, `"1"`, or `"true"` enables it; anything else —
 * including absence — leaves it off. An explicit value in `/config.json`
 * always wins over `VITE_DEBUG`, even when it disables debug mode.
 */
export function isDebugEnabled(): boolean {
  if (runtimeConfig?.debug !== undefined) {
    return isFlagEnabled(runtimeConfig.debug);
  }
  return isFlagEnabled(import.meta.env.VITE_DEBUG);
}

// =============================================================================
// Mock Mode
// =============================================================================

/**
 * Whether global mock mode is enabled via the `VITE_USE_MOCKS` variable.
 *
 * When `true`, all API domains use Mock implementations regardless of their
 * `backendReady` status. See the API client registry (`api/registry.ts`).
 */
export function isGlobalMockMode(): boolean {
  return import.meta.env.VITE_USE_MOCKS === 'true';
}
